import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../services/supabase';
import { toast } from 'sonner';

const AuthCallback = () => {
  console.log('🎯 AuthCallback component mounted - URL:', window.location.href);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInvitation = searchParams.get('invitation') === 'true';

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('🔄 === INICIO DEL PROCESO DE CALLBACK === 🔄');
        console.log('🔍 URL completa:', window.location.href);
        console.log('🌍 Origin:', window.location.origin);
        console.log('🛣️ Pathname:', window.location.pathname);
        console.log('❓ Search params:', window.location.search);
        console.log('🔍 Es invitación:', isInvitation);
        console.log('🔐 Local Storage:', {
          supabaseAuth: localStorage.getItem('supabase.auth.token'),
          pendingJoinBusinessId: localStorage.getItem('pendingJoinBusinessId'),
          allKeys: Object.keys(localStorage)
        });
        
        // Procesar el hash manualmente si existe
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        if (hashParams.has('access_token')) {
          console.log('🎟️ Token encontrado, estableciendo sesión manualmente...');
          const { data, error: setSessionError } = await supabase.auth.setSession({
            access_token: hashParams.get('access_token'),
            refresh_token: hashParams.get('refresh_token')
          });
          
          if (setSessionError) {
            console.error('❌ Error al establecer sesión:', setSessionError);
            throw setSessionError;
          }
          console.log('✅ Sesión establecida manualmente');
        }
        
        // Obtener la sesión
        console.log('⏳ Intentando obtener sesión...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        console.log('📦 Respuesta completa:', { 
          session: session ? {
            ...session,
            user: {
              ...session.user,
              id: session.user.id,
              email: session.user.email,
              app_metadata: session.user.app_metadata
            }
          } : null, 
          error: sessionError ? {
            message: sessionError.message,
            status: sessionError.status,
            name: sessionError.name
          } : null
        });
        
        if (sessionError) {
          console.error('❌ Error al obtener sesión:', sessionError);
          throw sessionError;
        }

        if (session?.user) {
          console.log('✅ Sesión establecida para:', session.user.email);
          
          // Verificar/crear perfil
          console.log('🔍 Buscando perfil existente...');
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          console.log('📋 Resultado búsqueda de perfil:', { profile, error: profileError });

          if (profileError && profileError.code === 'PGRST116') {
            console.log('🆕 Perfil no encontrado, creando uno nuevo...');
            // Obtener nombre y apellido del user_metadata de Google
            const fullName = session.user.user_metadata?.full_name || '';
            const [name = '', surname = ''] = fullName.split(' ');
            
            const { error: createError } = await supabase
              .from('profiles')
              .insert([{
                id: session.user.id,
                auth_user_id: session.user.id,
                role: 'owner',
                name,
                surname
              }])
              .select()
              .single();

            if (createError) {
              console.error('❌ Error al crear perfil:', createError);
            } else {
              console.log('✅ Perfil creado exitosamente');
            }
          } else if (!profileError && profile && (!profile.name || !profile.surname)) {
            // Si el perfil existe pero no tiene nombre o apellido, actualizarlo
            console.log('🔄 Actualizando perfil con datos de Google...');
            const fullName = session.user.user_metadata?.full_name || '';
            const [name = '', surname = ''] = fullName.split(' ');
            
            const { error: updateError } = await supabase
              .from('profiles')
              .update({ name, surname })
              .eq('id', session.user.id);

            if (updateError) {
              console.error('❌ Error al actualizar perfil:', updateError);
            } else {
              console.log('✅ Perfil actualizado exitosamente');
            }
          }

          // Procesar solicitud de unión a negocio si viene de una invitación
          if (isInvitation) {
            await processJoinRequest(session.user);
          }

          navigate('/');
        } else {
          console.log('⚠️ No se encontró sesión');
          navigate('/login');
        }
      } catch (err) {
        console.error('❌ Error en el proceso de callback:', {
          message: err.message,
          name: err.name,
          stack: err.stack,
          cause: err.cause
        });
        navigate('/login');
      }
    };

    // Función para procesar la solicitud de unión a un negocio
    const processJoinRequest = async (user) => {
      try {
        const businessId = localStorage.getItem('pendingJoinBusinessId');
        
        console.log('=== DEBUG JOIN REQUEST ===');
        console.log('1. ID from localStorage:', businessId);
        console.log('2. localStorage keys:', Object.keys(localStorage));
        console.log('3. All localStorage data:', Object.fromEntries(
          Object.keys(localStorage).map(key => [key, localStorage.getItem(key)])
        ));
        
        if (!businessId) {
          console.log('❌ No se encontró ID de negocio para la invitación');
          return;
        }
        
        console.log('🔄 Procesando solicitud de unión al negocio:', businessId);
        
        // 1. Verificar si el negocio existe
        const { data: business, error: businessError } = await supabase
          .from('businesses')
          .select('id, name')
          .eq('id', businessId)
          .single();
          
        console.log('4. Business query result:', { business, error: businessError });
        
        if (businessError) {
          console.error('❌ Error al obtener negocio:', businessError);
          throw businessError;
        }
        
        // 2. Verificar si ya es miembro
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();
          
        if (profileError && profileError.code !== 'PGRST116') {
          console.error('❌ Error al verificar perfil:', profileError);
          throw profileError;
        }
        
        if (profile) {
          // Verificar si ya es miembro del negocio usando business_members
          const { data: membership, error: membershipError } = await supabase
            .from('business_members')
            .select('id')
            .eq('business_id', businessId)
            .eq('profile_id', profile.id)
            .single();
            
          if (membershipError && membershipError.code !== 'PGRST116') {
            console.error('❌ Error al verificar membresía:', membershipError);
            throw membershipError;
          }
          
          if (membership) {
            console.log('⚠️ El usuario ya es miembro de este negocio');
            toast.info('Ya eres miembro de este negocio');
            return;
          }
        }
        
        // 3. Verificar si ya tiene una solicitud pendiente
        const { data: existingRequest, error: requestCheckError } = await supabase
          .from('business_join_requests')
          .select('id, status')
          .eq('business_id', businessId)
          .eq('user_id', user.id)
          .single();
          
        if (!requestCheckError && existingRequest) {
          if (existingRequest.status === 'pending') {
            console.log('⚠️ El usuario ya tiene una solicitud pendiente');
            toast.info('Ya tienes una solicitud pendiente para unirte a este negocio');
          } else if (existingRequest.status === 'rejected') {
            console.log('⚠️ La solicitud del usuario fue rechazada');
            toast.error('Tu solicitud para unirte a este negocio fue rechazada');
          }
          return;
        }
        
        // 4. Crear solicitud de unión
        const { error: requestError } = await supabase
          .from('business_join_requests')
          .insert([{
            business_id: businessId,
            user_id: user.id,
            status: 'pending',
            user_email: user.email,
            user_name: user.user_metadata?.name || user.email.split('@')[0]
          }]);
          
        if (requestError) {
          console.error('❌ Error al crear solicitud:', requestError);
          throw requestError;
        }
        
        console.log('✅ Solicitud de unión creada exitosamente');
        toast.success(`Solicitud enviada a ${business.name}`);
        
        // Limpiar el localStorage después de procesar
        localStorage.removeItem('pendingJoinBusinessId');
        
      } catch (error) {
        console.error('❌ Error al procesar solicitud de unión:', error);
        toast.error('Error al procesar tu solicitud');
      }
    };

    handleCallback();
  }, [navigate, isInvitation]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
        <p className="mt-4 text-gray-600">Procesando tu inicio de sesión...</p>
      </div>
    </div>
  );
};

export default AuthCallback; 